import { Plus, Tag, X } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { SUGGESTED_TAGS, addTag, normalizeTag, removeTag, tagTone } from "../lib/tags";

type Props = {
  tags: string[];
  // Tags already used elsewhere, surfaced as suggestions (union with the seeded list).
  availableTags?: string[];
  onChange: (tags: string[]) => void | Promise<void>;
  className?: string;
};

export function TagPicker({ tags, availableTags = [], onChange, className }: Props) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const suggestions = useMemo(() => {
    const pool = Array.from(new Set([...SUGGESTED_TAGS, ...availableTags]));
    const query = normalizeTag(draft);
    return pool
      .filter((tag) => !tags.includes(tag))
      .filter((tag) => (query ? tag.includes(query) : true))
      .slice(0, 8);
  }, [availableTags, draft, tags]);

  useEffect(() => {
    if (!open) {
      return;
    }
    const handleClick = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setOpen(false);
        setDraft("");
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [open]);

  const commit = async (next: string[]) => {
    setBusy(true);
    try {
      await onChange(next);
    } finally {
      setBusy(false);
    }
  };

  const apply = (tag: string) => {
    const next = addTag(tags, tag);
    if (next !== tags) {
      void commit(next);
    }
    setDraft("");
  };

  const drop = (tag: string) => {
    void commit(removeTag(tags, tag));
  };

  return (
    <div className={`relative inline-flex flex-wrap items-center gap-1 ${className ?? ""}`} ref={containerRef}>
      {tags.map((tag) => (
        <span
          key={tag}
          className={`inline-flex h-6 items-center gap-1 rounded-md border px-2 text-xs font-semibold ${tagTone(tag)}`}
        >
          {tag}
          <button
            type="button"
            disabled={busy}
            onClick={() => drop(tag)}
            aria-label={`Bỏ tag ${tag}`}
            className="opacity-60 hover:opacity-100 disabled:opacity-30"
          >
            <X size={11} aria-hidden="true" />
          </button>
        </span>
      ))}

      <button
        type="button"
        disabled={busy}
        onClick={() => setOpen((value) => !value)}
        title="Thêm tag"
        aria-label="Thêm tag"
        className="inline-flex h-6 items-center gap-1 rounded-md border border-dashed border-slate-300 px-1.5 text-xs font-semibold text-slate-500 hover:bg-slate-50 disabled:opacity-50"
      >
        {tags.length === 0 ? <Tag size={12} aria-hidden="true" /> : <Plus size={12} aria-hidden="true" />}
        {tags.length === 0 ? "Tag" : null}
      </button>

      {open ? (
        <div className="absolute right-0 top-7 z-20 w-56 overflow-hidden rounded-lg border border-line bg-white py-1 shadow-lg">
          <div className="px-2 pb-1">
            <input
              autoFocus
              value={draft}
              onChange={(event) => setDraft(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  event.preventDefault();
                  if (normalizeTag(draft)) {
                    apply(draft);
                  }
                } else if (event.key === "Escape") {
                  setOpen(false);
                  setDraft("");
                }
              }}
              placeholder="Tìm hoặc tạo tag…"
              className="h-7 w-full rounded-md border border-line px-2 text-xs outline-none focus:border-brand"
            />
          </div>

          <ul className="max-h-52 overflow-auto py-0.5">
            {suggestions.map((tag) => (
              <li key={tag}>
                <button
                  type="button"
                  onClick={() => apply(tag)}
                  className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-xs font-semibold text-slate-700 hover:bg-slate-50"
                >
                  <span className={`inline-flex h-5 items-center rounded border px-1.5 ${tagTone(tag)}`}>{tag}</span>
                </button>
              </li>
            ))}
            {normalizeTag(draft) && !suggestions.includes(normalizeTag(draft)) && !tags.includes(normalizeTag(draft)) ? (
              <li>
                <button
                  type="button"
                  onClick={() => apply(draft)}
                  className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-xs font-semibold text-brand hover:bg-blue-50"
                >
                  <Plus size={13} aria-hidden="true" />
                  Tạo tag “{normalizeTag(draft)}”
                </button>
              </li>
            ) : null}
            {suggestions.length === 0 && !normalizeTag(draft) ? (
              <li className="px-3 py-1.5 text-xs text-slate-400">Gõ để tạo tag mới…</li>
            ) : null}
          </ul>
        </div>
      ) : null}
    </div>
  );
}
